/**
 * VoiceFactory - Creates and manages Tone.js synthesizer voices
 * Ported from js/voices.js with TypeScript types
 * Optimized with voice pooling for performance
 */

import * as Tone from 'tone';
import type { InstrumentType, ADSRSettings } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import { instrumentDatabase } from '../instruments/InstrumentDatabase';
import { validateInstrumentParameters } from '../../utils/parameterValidation';

/**
 * Voice interface representing a synthesizer voice with trigger function and gain control
 */
export interface Voice {
  /** Function to trigger a note at a specific time */
  trigger: (time: number, note: string | null, duration?: string | number) => void;
  /** Gain node for volume control */
  gainNode: Tone.Gain;
  /** Update parameters in real-time */
  updateParameters: (parameters: InstrumentParameters) => void;
  /** Dispose of all audio resources */
  dispose: () => void;
}

/**
 * Voice pool for reusing voice instances
 * Maps instrument type to available voice instances
 */
const voicePool: Map<InstrumentType, Voice[]> = new Map();
const MAX_POOL_SIZE = 3; // Maximum voices to keep in pool per instrument type

/**
 * Get a voice from the pool or create a new one
 */
function getVoiceFromPool(
  instrumentType: InstrumentType,
  parameters: InstrumentParameters
): Voice | null {
  const pool = voicePool.get(instrumentType);
  if (pool && pool.length > 0) {
    const voice = pool.pop()!;
    // Update parameters for reused voice
    voice.updateParameters(parameters);
    return voice;
  }
  return null;
}

/**
 * Return a voice to the pool for reuse
 */
function returnVoiceToPool(instrumentType: InstrumentType, voice: Voice): void {
  const pool = voicePool.get(instrumentType) || [];
  if (pool.length < MAX_POOL_SIZE) {
    pool.push(voice);
    voicePool.set(instrumentType, pool);
  } else {
    // Pool is full, dispose the voice
    voice.dispose();
  }
}

/**
 * Clear the voice pool (useful for cleanup)
 */
export function clearVoicePool(): void {
  voicePool.forEach((pool) => {
    pool.forEach((voice) => voice.dispose());
  });
  voicePool.clear();
}

/**
 * Default ADSR envelope settings
 */
const DEFAULT_ENVELOPE: ADSRSettings = {
  attack: 0.01,
  decay: 0.1,
  sustain: 0.5,
  release: 0.1,
};

/**
 * Noise envelope settings (short attack/decay, no sustain)
 */
const NOISE_ENVELOPE: ADSRSettings = {
  attack: 0.001,
  decay: 0.05,
  sustain: 0,
  release: 0,
};

/**
 * Validate parameters with error handling
 */
function validateParameters(params: InstrumentParameters): InstrumentParameters {
  try {
    return validateInstrumentParameters(params);
  } catch (error) {
    console.error('Error validating parameters:', error);
    return {
      adsr: DEFAULT_ENVELOPE,
      oscillator: {},
      filter: {},
      effects: { bitCrush: 4 },
    };
  }
}

/**
 * Create a fallback voice when voice creation fails
 */
function createFallbackVoice(row: number): Voice {
  console.warn(`Creating fallback voice for row ${row}`);
  
  try {
    const gainNode = new Tone.Gain(0.8).toDestination();
    const bitCrusher = new Tone.BitCrusher({ bits: 4 }).connect(gainNode);
    const synth = new Tone.Synth({ 
      oscillator: { type: 'square' }, 
      envelope: DEFAULT_ENVELOPE 
    });
    synth.connect(bitCrusher);

    const trigger = (time: number, note: string | null, duration: string | number = '16n') => {
      try {
        if (note && typeof note === 'string') {
          synth.triggerAttackRelease(note, duration, time);
        }
      } catch (error) {
        console.error('Error triggering fallback voice:', error);
      }
    };

    const updateParameters = () => {
      console.warn('Parameter updates not supported on fallback voice');
    };

    const dispose = () => {
      try {
        synth.dispose();
        bitCrusher.dispose();
        gainNode.dispose();
      } catch (error) {
        console.error('Error disposing fallback voice:', error);
      }
    };

    return { trigger, gainNode, updateParameters, dispose };
  } catch (error) {
    console.error('Critical error creating fallback voice:', error);
    // Return a minimal no-op voice as last resort
    return {
      trigger: () => {},
      gainNode: new Tone.Gain(0).toDestination(),
      updateParameters: () => {},
      dispose: () => {},
    };
  }
}

/**
 * Creates an array of Tone.js voices based on instrument types and parameters
 * Each voice is bit-crushed for 8-bit sound and connected to destination
 *
 * @param instrumentTypes - Array of instrument type strings
 * @param adsrSettings - Optional array of ADSR envelope settings per instrument (legacy support)
 * @param instrumentParameters - Optional array of full instrument parameters per instrument
 * @param parametersEnabled - Optional array of flags indicating whether to apply parameters per instrument
 * @returns Array of Voice objects with trigger functions and gain nodes
 */
export function createVoices(
  instrumentTypes: InstrumentType[],
  adsrSettings: ADSRSettings[] = [],
  instrumentParameters: (InstrumentParameters | undefined)[] = [],
  parametersEnabled: boolean[] = []
): Voice[] {
  return instrumentTypes.map((type, row) => {
    try {
      return createSingleVoice(type, row, adsrSettings, instrumentParameters, parametersEnabled);
    } catch (error) {
      console.error(`Error creating voice for instrument ${type} at row ${row}:`, error);
      // Return a fallback voice with basic square wave
      return createFallbackVoice(row);
    }
  });
}

/**
 * Create a single voice with error handling
 */
function createSingleVoice(
  type: InstrumentType,
  row: number,
  adsrSettings: ADSRSettings[],
  instrumentParameters: (InstrumentParameters | undefined)[],
  parametersEnabled: boolean[]
): Voice {
  try {
      // Check if parameters should be applied for this row
      const shouldApplyParameters = parametersEnabled?.[row] ?? false;
      
      // Get parameters for this instrument
      const params = shouldApplyParameters ? (instrumentParameters[row] || {}) : {};
      const metadata = instrumentDatabase.getInstrument(type);
      
      // Validate that instrument exists
      if (!metadata) {
        console.warn(`Unknown instrument type: ${type}. Using fallback.`);
        throw new Error(`Unknown instrument: ${type}`);
      }
      
      const defaultParams = shouldApplyParameters ? (metadata.defaultParameters || {}) : {};
      
      // Merge parameters: provided params override defaults (only if parameters are enabled)
      const mergedParams: InstrumentParameters = shouldApplyParameters ? {
        adsr: { ...DEFAULT_ENVELOPE, ...defaultParams.adsr, ...params.adsr },
        oscillator: { ...defaultParams.oscillator, ...params.oscillator },
        filter: { ...defaultParams.filter, ...params.filter },
        effects: { ...defaultParams.effects, ...params.effects },
      } : {
        adsr: DEFAULT_ENVELOPE,
        oscillator: {},
        filter: {},
        effects: { bitCrush: 4 }, // Keep default bit crusher for 8-bit sound
      };
      
      // Validate parameters
      const validatedParams = validateParameters(mergedParams);
      
      // Try to get voice from pool
      const pooledVoice = getVoiceFromPool(type, validatedParams);
      if (pooledVoice) {
        return pooledVoice;
      }
      
      // Create gain node and bit crusher for 8-bit sound
      const gainNode = new Tone.Gain(0.8).toDestination();
      
      // Legacy support: use adsrSettings if provided, otherwise use validated params
      const env: ADSRSettings = adsrSettings[row] || (validatedParams.adsr as ADSRSettings);
      
      // Create bit crusher with configurable bits (default 4 for 8-bit sound)
      const bitCrusherBits = Math.round(Math.max(1, Math.min(16, validatedParams.effects?.bitCrush ?? 4)));
      const bitCrusher = new Tone.BitCrusher({ bits: bitCrusherBits }).connect(gainNode);

      let synth: Tone.Synth | Tone.FMSynth | Tone.AMSynth | Tone.MetalSynth | 
                 Tone.PluckSynth | Tone.DuoSynth | Tone.Sampler | 
                 Tone.MembraneSynth | Tone.NoiseSynth;
      
      // Optional filter and effects nodes
      let filter: Tone.Filter | null = null;
      let distortion: Tone.Distortion | null = null;
      let chorus: Tone.Chorus | null = null;

      // Create synthesizer based on instrument type
      switch (type) {
      case 'square':
        synth = new Tone.Synth({ oscillator: { type: 'square' }, envelope: env });
        break;

      case 'triangle':
        synth = new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: env });
        break;

      case 'sawtooth':
        synth = new Tone.Synth({ oscillator: { type: 'sawtooth' }, envelope: env });
        break;

      case 'pulse25':
      case 'pulse50':
      case 'pulse75': {
        const width = type === 'pulse25' ? 0.25 : type === 'pulse50' ? 0.5 : 0.75;
        synth = new Tone.Synth({ 
          oscillator: { type: 'pulse', width }, 
          envelope: env 
        });
        break;
      }

      case 'fmsynth':
        synth = new Tone.FMSynth({ modulationIndex: 12, envelope: env });
        break;

      case 'amsynth':
        synth = new Tone.AMSynth({ harmonicity: 3, envelope: env });
        break;

      case 'metal':
        synth = new Tone.MetalSynth({ envelope: env });
        break;

      case 'pluck':
        synth = new Tone.PluckSynth({ 
          attackNoise: 0.1, 
          dampening: 4000, 
          resonance: 0.8 
        });
        break;

      case 'duosynth':
        synth = new Tone.DuoSynth({
          voice0: { oscillator: { type: 'square' }, envelope: env },
          voice1: { oscillator: { type: 'sawtooth' }, envelope: env },
        });
        break;

      case 'sampler':
        synth = new Tone.Sampler({
          urls: { 
            C4: '8bit_C4.mp3', 
            D4: '8bit_D4.mp3', 
            E4: '8bit_E4.mp3' 
          },
          onload: () => console.log('Sampler loaded'),
        });
        break;

      case 'membrane':
        synth = new Tone.MembraneSynth({
          envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0 },
        });
        break;

      case 'noise-white':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'white' }, 
          envelope: NOISE_ENVELOPE 
        });
        break;

      case 'noise-pink':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'pink' }, 
          envelope: NOISE_ENVELOPE 
        });
        break;

      case 'drum-kick':
        synth = new Tone.MembraneSynth({
          pitchDecay: 0.05,
          envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0 },
        });
        break;

      case 'drum-snare':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'white' }, 
          envelope: NOISE_ENVELOPE 
        });
        break;

      case 'drum-tom':
        synth = new Tone.MembraneSynth({
          pitchDecay: 0.02,
          envelope: { attack: 0.005, decay: 0.3, sustain: 0, release: 0 },
        });
        break;

      case 'drum-hat':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'pink' }, 
          envelope: NOISE_ENVELOPE 
        });
        break;

      // Additional percussion
      case 'drum-hat-open':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'pink' }, 
          envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.2 }
        });
        break;

      case 'drum-tom-low':
        synth = new Tone.MembraneSynth({
          pitchDecay: 0.03,
          octaves: 4,
          envelope: { attack: 0.001, decay: 0.4, sustain: 0, release: 0 },
        });
        break;

      case 'drum-tom-mid':
        synth = new Tone.MembraneSynth({
          pitchDecay: 0.02,
          octaves: 6,
          envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0 },
        });
        break;

      case 'drum-tom-high':
        synth = new Tone.MembraneSynth({
          pitchDecay: 0.015,
          octaves: 8,
          envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0 },
        });
        break;

      case 'drum-cymbal-crash':
        synth = new Tone.MetalSynth({
          envelope: { attack: 0.001, decay: 0.5, sustain: 0, release: 0.5 },
          harmonicity: 5.1,
          modulationIndex: 32,
          resonance: 4000,
          octaves: 1.5,
        });
        break;

      case 'drum-cymbal-ride':
        synth = new Tone.MetalSynth({
          envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.3 },
          harmonicity: 3.5,
          modulationIndex: 20,
          resonance: 3000,
          octaves: 1,
        });
        break;

      case 'drum-clap':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'white' }, 
          envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.08 }
        });
        break;

      // Pad instruments
      case 'pad-choir':
        synth = new Tone.Synth({ 
          oscillator: { type: 'sawtooth' }, 
          envelope: { attack: 0.6, decay: 0.3, sustain: 0.85, release: 1.2 }
        });
        break;

      case 'pad-sweep':
        synth = new Tone.Synth({ 
          oscillator: { type: 'sawtooth' }, 
          envelope: { attack: 0.7, decay: 0.4, sustain: 0.8, release: 1.5 }
        });
        break;

      case 'pad-drone':
        synth = new Tone.Synth({ 
          oscillator: { type: 'sine' }, 
          envelope: { attack: 1.0, decay: 0.5, sustain: 1.0, release: 2.0 }
        });
        break;

      // Arp instruments
      case 'arp-fast':
        synth = new Tone.Synth({ 
          oscillator: { type: 'square' }, 
          envelope: { attack: 0.001, decay: 0.03, sustain: 0.2, release: 0.03 }
        });
        break;

      case 'arp-slow':
        synth = new Tone.Synth({ 
          oscillator: { type: 'triangle' }, 
          envelope: { attack: 0.01, decay: 0.1, sustain: 0.6, release: 0.1 }
        });
        break;

      case 'arp-random':
        synth = new Tone.Synth({ 
          oscillator: { type: 'pulse', width: 0.3 }, 
          envelope: { attack: 0.005, decay: 0.05, sustain: 0.3, release: 0.05 }
        });
        break;

      case 'arp-chord':
        synth = new Tone.DuoSynth({
          voice0: { oscillator: { type: 'sawtooth' }, envelope: { attack: 0.01, decay: 0.08, sustain: 0.5, release: 0.08 } },
          voice1: { oscillator: { type: 'sawtooth' }, envelope: { attack: 0.01, decay: 0.08, sustain: 0.5, release: 0.08 } },
          vibratoAmount: 0.5,
          vibratoRate: 5,
        });
        break;

      // Bass instruments
      case 'bass-sub':
        synth = new Tone.Synth({ 
          oscillator: { type: 'sine' }, 
          envelope: { attack: 0.02, decay: 0.25, sustain: 0.8, release: 0.4 }
        });
        break;

      // SFX instruments
      case 'sfx-laser':
        synth = new Tone.Synth({ 
          oscillator: { type: 'square' }, 
          envelope: { attack: 0.001, decay: 0.15, sustain: 0, release: 0.15 }
        });
        break;

      case 'sfx-explosion':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'brown' }, 
          envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.3 }
        });
        break;

      case 'sfx-coin':
        synth = new Tone.Synth({ 
          oscillator: { type: 'square' }, 
          envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.1 }
        });
        break;

      case 'sfx-jump':
        synth = new Tone.Synth({ 
          oscillator: { type: 'triangle' }, 
          envelope: { attack: 0.001, decay: 0.12, sustain: 0, release: 0.12 }
        });
        break;

      case 'sfx-powerup':
        synth = new Tone.FMSynth({ 
          modulationIndex: 15,
          envelope: { attack: 0.001, decay: 0.25, sustain: 0, release: 0.25 }
        });
        break;

      case 'sfx-hit':
        synth = new Tone.NoiseSynth({ 
          noise: { type: 'white' }, 
          envelope: { attack: 0.001, decay: 0.08, sustain: 0, release: 0.08 }
        });
        break;

      case 'sfx-blip':
        synth = new Tone.Synth({ 
          oscillator: { type: 'sine' }, 
          envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.05 }
        });
        break;

        default:
          console.warn(`Unknown instrument type: ${type}, using square wave`);
          synth = new Tone.Synth({ 
            oscillator: { type: 'square' }, 
            envelope: env 
          });
      }

      // Build effects chain: synth -> filter -> distortion -> chorus -> bitCrusher -> gain
      let currentNode: Tone.ToneAudioNode = synth;
      
      // Apply filter if specified
      if (validatedParams.filter && validatedParams.filter.frequency) {
        try {
          filter = new Tone.Filter({
            type: validatedParams.filter.type || 'lowpass',
            frequency: validatedParams.filter.frequency,
            Q: validatedParams.filter.resonance || 1,
          });
          currentNode.connect(filter);
          currentNode = filter;
        } catch (error) {
          console.error('Error creating filter:', error);
        }
      }
      
      // Apply distortion if specified
      if (validatedParams.effects?.distortion && validatedParams.effects.distortion > 0) {
        try {
          distortion = new Tone.Distortion(validatedParams.effects.distortion);
          currentNode.connect(distortion);
          currentNode = distortion;
        } catch (error) {
          console.error('Error creating distortion:', error);
        }
      }
      
      // Apply chorus if specified
      if (validatedParams.effects?.chorus) {
        try {
          chorus = new Tone.Chorus({ frequency: 1.5, delayTime: 3.5, depth: 0.7 }).start();
          currentNode.connect(chorus);
          currentNode = chorus;
        } catch (error) {
          console.error('Error creating chorus:', error);
        }
      }
      
      // Connect to bit crusher
      currentNode.connect(bitCrusher);

      // Create trigger function with error handling
      const trigger = (time: number, note: string | null, duration: string | number = '16n') => {
        try {
          if (synth instanceof Tone.NoiseSynth) {
            // Noise synths don't take a note parameter
            synth.triggerAttackRelease(duration, time);
          } else if (note && typeof note === 'string') {
            // All other synths require a note
            synth.triggerAttackRelease(note, duration, time);
          }
        } catch (error) {
          console.error(`Error triggering voice for ${type}:`, error);
        }
      };
      
      // Create updateParameters function for real-time parameter updates with validation
      const updateParameters = (newParams: InstrumentParameters) => {
        try {
          // Validate parameters first
          const validatedNewParams = validateParameters(newParams);
          
          // Update ADSR envelope
          if (validatedNewParams.adsr && 'envelope' in synth) {
            try {
              const envelope = synth.envelope as Tone.Envelope;
              if (validatedNewParams.adsr.attack !== undefined) {
                envelope.attack = Math.max(0.001, Math.min(2.0, validatedNewParams.adsr.attack));
              }
              if (validatedNewParams.adsr.decay !== undefined) {
                envelope.decay = Math.max(0.001, Math.min(2.0, validatedNewParams.adsr.decay));
              }
              if (validatedNewParams.adsr.sustain !== undefined) {
                envelope.sustain = Math.max(0.0, Math.min(1.0, validatedNewParams.adsr.sustain));
              }
              if (validatedNewParams.adsr.release !== undefined) {
                envelope.release = Math.max(0.001, Math.min(2.0, validatedNewParams.adsr.release));
              }
            } catch (error) {
              console.warn('Error updating ADSR envelope:', error);
            }
          }
          
          // Update oscillator settings
          if (validatedNewParams.oscillator && 'oscillator' in synth) {
            try {
              const osc = synth.oscillator as any;
              if (validatedNewParams.oscillator.type && osc.type !== undefined) {
                osc.type = validatedNewParams.oscillator.type;
              }
              if (validatedNewParams.oscillator.pulseWidth !== undefined && 'width' in osc) {
                osc.width.value = Math.max(0.0, Math.min(1.0, validatedNewParams.oscillator.pulseWidth));
              }
              if (validatedNewParams.oscillator.detune !== undefined) {
                osc.detune.value = Math.max(-1200, Math.min(1200, validatedNewParams.oscillator.detune));
              }
            } catch (error) {
              console.warn('Error updating oscillator settings:', error);
            }
          }
          
          // Update FM-specific parameters
          if (synth instanceof Tone.FMSynth && validatedNewParams.oscillator) {
            try {
              if (validatedNewParams.oscillator.harmonicity !== undefined) {
                synth.harmonicity.value = Math.max(0.5, Math.min(8.0, validatedNewParams.oscillator.harmonicity));
              }
              if (validatedNewParams.oscillator.modulationIndex !== undefined) {
                synth.modulationIndex.value = Math.max(0, Math.min(20, validatedNewParams.oscillator.modulationIndex));
              }
            } catch (error) {
              console.warn('Error updating FM parameters:', error);
            }
          }
          
          // Update filter settings
          if (filter && validatedNewParams.filter) {
            try {
              if (validatedNewParams.filter.frequency !== undefined) {
                filter.frequency.value = Math.max(20, Math.min(20000, validatedNewParams.filter.frequency));
              }
              if (validatedNewParams.filter.resonance !== undefined) {
                filter.Q.value = Math.max(0.1, Math.min(10, validatedNewParams.filter.resonance));
              }
              if (validatedNewParams.filter.type) {
                filter.type = validatedNewParams.filter.type;
              }
            } catch (error) {
              console.warn('Error updating filter settings:', error);
            }
          }
          
          // Update effects settings
          if (validatedNewParams.effects) {
            try {
              // Note: BitCrusher bits property is read-only and cannot be updated in real-time
              // A full voice recreation would be needed to change bit depth
              
              // Update distortion
              if (distortion && validatedNewParams.effects.distortion !== undefined) {
                distortion.distortion = Math.max(0, Math.min(1, validatedNewParams.effects.distortion));
              }
            } catch (error) {
              console.warn('Error updating effects settings:', error);
            }
          }
        } catch (error) {
          console.error('Error updating voice parameters:', error);
        }
      };
      
      // Create dispose function for cleanup
      const dispose = () => {
        try {
          synth.dispose();
          bitCrusher.dispose();
          if (filter) filter.dispose();
          if (distortion) distortion.dispose();
          if (chorus) chorus.dispose();
          gainNode.dispose();
        } catch (error) {
          console.warn('Error disposing voice:', error);
        }
      };

      const voice: Voice = { trigger, gainNode, updateParameters, dispose };
      
      // Note: We don't return voices to pool immediately on creation
      // They will be returned when replaced/disposed by the application
      
      return voice;
    } catch (error) {
      console.error(`Error in voice creation for ${type}:`, error);
      throw error; // Re-throw to be caught by outer try-catch
    }
}

/**
 * Dispose voices and optionally return them to the pool
 * @param voices - Array of voices to dispose
 * @param instrumentTypes - Array of instrument types (for pooling)
 * @param returnToPool - Whether to return voices to pool for reuse
 */
export function disposeVoices(
  voices: Voice[],
  instrumentTypes: InstrumentType[],
  returnToPool: boolean = true
): void {
  voices.forEach((voice, index) => {
    if (returnToPool && instrumentTypes[index]) {
      returnVoiceToPool(instrumentTypes[index], voice);
    } else {
      voice.dispose();
    }
  });
}
